import axios from 'axios';
import { isNil } from 'lodash';

import { DESIGN_API_URL } from '../lib/environment.js';

import { escapeRegExp } from '@bay-ui/BayPolotnoEditor/utils';

/**
 * Ambit value for Email Block Library categories. The API enforces no enum and
 * no casing on 'ambit', so every caller reads it from here rather than typing
 * the string — a silent typo would just return an empty category list.
 */
export const BLOCK_LIBRARY_AMBIT = 'blockLibrary';

/**
 * The body `POST /tags$General` actually accepts.
 *
 * Per Todd: tags do not have owners — TagSets do, and a tag points at one. So an
 * `owner` on the tag itself is rejected with a 422 (monday 13000827516, adding
 * the keyword "Columbus Day"). `tagSet` is either the TagSet's own `_id` or a
 * `{ name, owner }` descriptor naming that owner's set; both are valid, and the
 * descriptor is how a caller targets a company other than the acting one.
 *
 * Normalizing here rather than at a call site because all three callers of
 * createGeneralTag were sending `owner` — the template editor's Save > Create
 * Tags flow (the reported one), KeywordManagement, and TagManagement — so
 * fixing any one of them would have left the others posting a 422.
 *
 * `tagSet` may also arrive as the tag-set dropdown's option object
 * (`{ ...tagSet, label: name, value: _id }`); posting that whole object where an
 * id belongs was the other half of the reported failure.
 *
 * NOTE: `tags$Category` is different and does take `owner` — a customer has one
 * Category TagSet, so the API resolves it from the owner alone. Do not "fix"
 * createCategory to match this.
 */
export const normalizeGeneralTagPayload = (tagObject) => {
	const { owner, tagSet, ...rest } = tagObject;

	// Anything carrying an id collapses to that id; a `{ name, owner }` descriptor
	// carries neither, so it passes through untouched.
	const normalizedTagSet =
		tagSet && typeof tagSet === 'object'
			? (tagSet.value ?? tagSet._id ?? tagSet)
			: tagSet;

	return { ...rest, tagSet: normalizedTagSet };
};

const tags = () => {
	let tagsCancelToken = axios.CancelToken.source();

	return {
		cancel: () => {
			tagsCancelToken.cancel('Operation canceled by the user.');
			tagsCancelToken = axios.CancelToken.source();
		},

		create: (data) => {
			return new Promise((resolve, reject) => {
				const controller = new AbortController();

				let timeout = setTimeout(() => {
					controller.abort();
				}, 180000);

				axios({
					url: `${DESIGN_API_URL}/tags`,
					method: 'POST',
					cache: 'no-cache',
					data: data,
					signal: controller.signal,
					cancelToken: tagsCancelToken.token,
				})
					.then((resp) => {
						clearTimeout(timeout);

						resolve(resp.data.result);
					})
					.catch((err) => {
						clearTimeout(timeout);

						reject(err);
					});
			});
		},

		createCategory: (tagObject) => {
			return new Promise((resolve, reject) => {
				const controller = new AbortController();

				let timeout = setTimeout(() => {
					controller.abort();
				}, 180000);

				axios({
					url: `${DESIGN_API_URL}/tags$Category`,
					method: 'POST',
					cache: 'no-cache',
					data: tagObject,
					signal: controller.signal,
				})
					.then((resp) => {
						clearTimeout(timeout);

						resolve(resp.data.result);
					})
					.catch((err) => {
						clearTimeout(timeout);

						reject(err);
					});
			});
		},

		createGeneralTag: (tagObject) => {
			return new Promise((resolve, reject) => {
				const controller = new AbortController();

				let timeout = setTimeout(() => {
					controller.abort();
				}, 180000);

				axios({
					url: `${DESIGN_API_URL}/tags$General`,
					method: 'POST',
					cache: 'no-cache',
					data: normalizeGeneralTagPayload(tagObject),
					signal: controller.signal,
				})
					.then((resp) => {
						clearTimeout(timeout);

						resolve(resp.data.result);
					})
					.catch((err) => {
						clearTimeout(timeout);

						reject(err);
					});
			});
		},

		read: (limit, offset) => {
			return new Promise((resolve, reject) => {
				limit = limit || 24;
				offset = offset || 0;

				const controller = new AbortController();

				let timeout = setTimeout(() => {
					controller.abort();
				}, 180000);

				axios({
					url: `${DESIGN_API_URL}/tags?limit=${limit}&offset=${offset}`,
					method: 'GET',
					cache: 'no-cache',
					signal: controller.signal,
					cancelToken: tagsCancelToken.token,
				})
					.then((resp) => {
						clearTimeout(timeout);

						resolve(resp.data);
					})
					.catch((err) => {
						clearTimeout(timeout);

						reject(err);
					});
			});
		},

		readGeneral: (queryVals) => {
			return new Promise((resolve, reject) => {
				let { ownerId, searchTerm, includeHidden, limit, sort } = queryVals;

				let queryParams = {};

				const controller = new AbortController();

				let timeout = setTimeout(() => {
					controller.abort();
				}, 180000);

				if (ownerId) {
					queryParams = {
						$and: [
							+ownerId === 0 // 0 is a special case for the system owner
								? {
										'tagSet.owner.idNumber': +ownerId, // idNumber is a number, _id is a string
									}
								: {
										'tagSet.owner._id': ownerId,
									},
						],
					};
				}

				// search: (params?.search ? params.search.split(' ').map((e) => escapeRegExp(e)).join('.*') : ""),
				// "name":{"$regex":"Listing.*","$options":"i"}}
				if (!isNil(searchTerm) && searchTerm !== '') {
					queryParams.name = {
						$regex: `${
							searchTerm
								? searchTerm
										?.split(' ')
										.map((e) => escapeRegExp(e))
										.join('.*')
								: ''
						}`,
						$options: 'i',
					};
				}

				if (includeHidden === false) {
					queryParams = {
						$and: [...(queryParams['$and'] ?? []), { isHidden: false }],
					};
				}

				axios({
					url: `${DESIGN_API_URL}/tags$General?q=${JSON.stringify(queryParams)}
									&limit=${limit || null}
									&sort=${JSON.stringify(sort) || '{"dtUpd":"asc"}'}`,
					method: 'GET',
					cache: 'no-cache',
					signal: controller.signal,
				})
					.then((resp) => {
						clearTimeout(timeout);

						resolve(resp.data);
					})
					.catch((err) => {
						clearTimeout(timeout);

						reject(err);
					});
			});
		},

		readCategories: (queryVals) => {
			return new Promise((resolve, reject) => {
				let { ownerId, searchTerm, limit, sort, ambit } = queryVals;

				let queryParams = {};

				const controller = new AbortController();

				let timeout = setTimeout(() => {
					controller.abort();
				}, 180000);

				if (ownerId && ownerId !== 'null') {
					queryParams = {
						$and: [
							Number(ownerId) === 0 // 0 is a special case for the system owner
								? {
										'tagSet.owner.idNumber': +ownerId, // idNumber is a number, _id is a string
									}
								: {
										'tagSet.owner._id': ownerId,
									},
						],
					};
				}

				// search: (params?.search ? params.search.split(' ').map((e) => escapeRegExp(e)).join('.*') : ""),
				// "name":{"$regex":"Listing.*","$options":"i"}}
				if (!isNil(searchTerm) && searchTerm !== '') {
					queryParams.name = {
						$regex: `${
							searchTerm
								? searchTerm
										?.split(' ')
										.map((e) => escapeRegExp(e))
										.join('.*')
								: ''
						}`,
						$options: 'i',
					};
				}

				/**
				 * `isHidden` is NOT a field on a tags$Category record, so querying it
				 * matched nothing and every caller that passed `false` got an empty
				 * list back — which is what left the Block Library category pickers
				 * showing "No options". Hidden state is a per-company overlay: the
				 * record carries `prefsOf.customers.CURRENT.hide` (what createCategory
				 * writes), and the categories page layers the KVStore value on top in
				 * reconcileCategoryData. Filter on it there, after the read, never
				 * here. Callers may still pass `isHidden`; like `presentOnHomepage`
				 * it is ignored, which is exactly what it did for every caller that
				 * passed `true`.
				 */

				/**
				 * Which side of the product a category belongs to. One array property
				 * on the tag ('emailTemplates', 'blockLibrary', ...) rather than a
				 * second tag set, per Todd. Pass a plain string to match membership,
				 * or a mongo operator object ({ $in: [...] } / { $nin: [...] }).
				 *
				 * NOTE: there is no index on this field yet, and legacy categories
				 * carry no ambit at all — so template reads deliberately stay
				 * unfiltered rather than asking for 'emailTemplates' and losing every
				 * category created before this existed.
				 */
				if (!isNil(ambit)) {
					queryParams.ambit = ambit;
				}

				axios({
					url: `${DESIGN_API_URL}/tags$Category?q=${JSON.stringify(queryParams)}
									&limit=${limit || null}
									&sort=${JSON.stringify(sort) || '{"dtUpd":"desc"}'}`,
					method: 'GET',
					cache: 'no-cache',
					signal: controller.signal,
					cancelToken: tagsCancelToken.token,
				})
					.then((resp) => {
						clearTimeout(timeout);

						resolve(resp.data);
					})
					.catch((err) => {
						clearTimeout(timeout);

						reject(err);
					});
			});
		},

		updateGeneralTag: (tagId, tagObject) => {
			return new Promise((resolve, reject) => {
				const controller = new AbortController();

				let timeout = setTimeout(() => {
					controller.abort();
				}, 180000);

				axios({
					url: `${DESIGN_API_URL}/tags$General/${tagId}`,
					method: 'PATCH',
					cache: 'no-cache',
					data: tagObject,
					signal: controller.signal,
				})
					.then((resp) => {
						clearTimeout(timeout);

						resolve(resp.data.result);
					})
					.catch((err) => {
						clearTimeout(timeout);

						reject(err);
					});
			});
		},

		updateCategory: (categoryId, tagObject) => {
			return new Promise((resolve, reject) => {
				const controller = new AbortController();

				let timeout = setTimeout(() => {
					controller.abort();
				}, 180000);

				axios({
					url: `${DESIGN_API_URL}/tags$Category/${categoryId}`,
					method: 'PATCH',
					cache: 'no-cache',
					data: tagObject,
					signal: controller.signal,
				})
					.then((resp) => {
						clearTimeout(timeout);

						resolve(resp.data.result);
					})
					.catch((err) => {
						clearTimeout(timeout);

						reject(err);
					});
			});
		},

		deleteGeneralTag: (tagId) => {
			return new Promise((resolve, reject) => {
				const controller = new AbortController();

				let timeout = setTimeout(() => {
					controller.abort();
				}, 180000);

				axios({
					url: `${DESIGN_API_URL}/tags$General/${tagId}`,
					method: 'DELETE',
					cache: 'no-cache',
					signal: controller.signal,
				})
					.then((resp) => {
						clearTimeout(timeout);

						resolve(resp.data.result);
					})
					.catch((err) => {
						clearTimeout(timeout);

						reject(err);
					});
			});
		},

		deleteCategory: (tagId) => {
			return new Promise((resolve, reject) => {
				const controller = new AbortController();

				let timeout = setTimeout(() => {
					controller.abort();
				}, 180000);

				axios({
					url: `${DESIGN_API_URL}/tags$Category/${tagId}`,
					method: 'DELETE',
					cache: 'no-cache',
					signal: controller.signal,
				})
					.then((resp) => {
						clearTimeout(timeout);

						resolve(resp.data.result);
					})
					.catch((err) => {
						clearTimeout(timeout);

						reject(err);
					});
			});
		},

		getCategoriesByOwner: (ownerId = null) => {
			return new Promise((resolve, reject) => {
				const controller = new AbortController();
				const hasOwnerParam =
					ownerId !== undefined && ownerId !== null && ownerId !== '';

				let timeout = setTimeout(() => {
					controller.abort();
				}, 180000);

				/**
				 * API HACK: Provide the param 'owner' for a meta.uiSupport containing ownerOptions;
				 * Omit 'owner' for a meta.uiSupport containing existingTags
				 * (for comparison of user input against existing tag names).
				 * Todd apologizes for this nastiness...
				 */
				const url = hasOwnerParam
					? `${DESIGN_API_URL}/tags$Category/NEW?owner=${ownerId}`
					: `${DESIGN_API_URL}/tags$Category/NEW`;

				axios({
					url: url,
					method: 'GET',
					cache: 'no-cache',
					signal: controller.signal,
					cancelToken: tagsCancelToken.token,
				})
					.then((resp) => {
						clearTimeout(timeout);

						resolve(resp.data);
					})
					.catch((err) => {
						clearTimeout(timeout);

						reject(err);
					});
			});
		},

		readKeywords: (queryVals) => {
			return new Promise((resolve, reject) => {
				let { ownerId, searchTerm, includeHidden, limit, sort } = queryVals;

				let queryParams = {
					$and: [
						{ 'tagSet.name': 'Keyword' }, // Filter for keywords only
					],
				};

				const controller = new AbortController();

				let timeout = setTimeout(() => {
					controller.abort();
				}, 180000);

				if (ownerId) {
					queryParams.$and.push(
						+ownerId === 0 // 0 is a special case for the system owner
							? { 'tagSet.owner.idNumber': +ownerId }
							: { 'tagSet.owner._id': ownerId },
					);
				}

				if (!isNil(searchTerm) && searchTerm !== '') {
					queryParams.name = {
						$regex: `${
							searchTerm
								? searchTerm
										?.split(' ')
										.map((e) => escapeRegExp(e))
										.join('.*')
								: ''
						}`,
						$options: 'i',
					};
				}

				if (includeHidden === false) {
					queryParams.$and.push({ isHidden: false });
				}

				axios({
					url: `${DESIGN_API_URL}/tags$General?q=${JSON.stringify(queryParams)}
									&limit=${limit || null}
									&sort=${JSON.stringify(sort) || '{"dtUpd":"asc"}'}`,
					method: 'GET',
					cache: 'no-cache',
					signal: controller.signal,
				})
					.then((resp) => {
						clearTimeout(timeout);

						resolve(resp.data);
					})
					.catch((err) => {
						clearTimeout(timeout);

						reject(err);
					});
			});
		},

		// Get tags that have a specific preset in their presets array
		readByPreset: (presetId) => {
			return new Promise((resolve, reject) => {
				const controller = new AbortController();

				let timeout = setTimeout(() => {
					controller.abort();
				}, 180000);

				const queryParams = {
					presets: {
						$in: [presetId],
					},
				};

				axios({
					url: `${DESIGN_API_URL}/tags$General?q=${JSON.stringify(queryParams)}`,
					method: 'GET',
					cache: 'no-cache',
					signal: controller.signal,
					cancelToken: tagsCancelToken.token,
				})
					.then((resp) => {
						clearTimeout(timeout);

						resolve(resp.data);
					})
					.catch((err) => {
						clearTimeout(timeout);

						reject(err);
					});
			});
		},
	};
};

const singleton = tags();
Object.freeze(singleton);

export default singleton;
